import React from 'react';
import { Plus, Info, BookOpen } from 'lucide-react';
import { useFoodContext } from '../context';
import { Button, Input } from '../components';
import { calculateGL } from '../utils/calculations';
import { getCategory, getCategoryColor, getCategoryEmoji } from '../utils/categories';

/**
 * Add Food Page Component - Manual food addition
 */
const AddFoodPage = () => {
  const {
    newFood,
    setNewFood,
    addNewFood
  } = useFoodContext();

  const handleInputChange = (field, value) => {
    setNewFood(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    addNewFood();
  };

  const previewGL = newFood.gi && newFood.carbs ? 
    calculateGL(parseFloat(newFood.gi), parseFloat(newFood.carbs)) : 0;
  const previewCategory = getCategory(previewGL);

  const isFormValid = newFood.name && newFood.gi && newFood.carbs;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
          <Plus className="w-6 h-6 text-orange-600" />
          Ajouter un nouvel aliment
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Food Name */}
          <Input
            label="Nom de l'aliment"
            placeholder="Ex: Pomme, Pain complet..."
            value={newFood.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
          />

          {/* Nutrition Values */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Indice Glycémique (IG)"
              type="number"
              placeholder="0-100"
              value={newFood.gi}
              onChange={(e) => handleInputChange('gi', e.target.value)}
              min="0"
              max="100"
              required
            />

            <Input
              label="Glucides (g/100g)"
              type="number"
              step="0.1"
              placeholder="Ex: 15.5"
              value={newFood.carbs}
              onChange={(e) => handleInputChange('carbs', e.target.value)}
              min="0"
              required
            />
          </div>

          {/* Preview */}
          {newFood.gi && newFood.carbs && (
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
              <h4 className="font-medium text-orange-800 mb-3">
                📊 Aperçu de l'aliment :
              </h4>
              
              <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                <div className="text-center">
                  <div className="text-orange-600 font-medium">IG</div>
                  <div className="text-2xl font-bold text-blue-600">{newFood.gi}</div>
                </div>
                <div className="text-center">
                  <div className="text-orange-600 font-medium">Glucides</div>
                  <div className="text-2xl font-bold text-green-600">{newFood.carbs}g</div>
                </div>
                <div className="text-center">
                  <div className="text-orange-600 font-medium">CG</div>
                  <div className="text-2xl font-bold text-purple-600">{previewGL}</div>
                </div>
              </div>
              
              <div className="text-center">
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(previewCategory)}`}>
                  {getCategoryEmoji(previewCategory)} Impact {previewCategory}
                </span>
              </div>
              
              {/* Tips based on category */}
              <div className="mt-3 p-3 bg-white rounded border border-orange-300">
                <div className="text-xs text-orange-700">
                  {previewGL <= 5 && (
                    <p>✅ Excellent choix ! Cet aliment a un impact glycémique très faible.</p>
                  )}
                  {previewGL > 5 && previewGL <= 10 && (
                    <p>👍 Bon aliment avec un impact glycémique faible.</p>
                  )}
                  {previewGL > 10 && previewGL <= 20 && (
                    <p>⚠️ Impact modéré. À consommer avec modération.</p>
                  )}
                  {previewGL > 20 && (
                    <p>🔴 Impact élevé. Limitez les portions ou associez avec des fibres.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={!isFormValid}
            variant="warning"
            size="lg"
            className="w-full"
          >
            <Plus className="w-5 h-5" />
            Ajouter l'aliment
          </Button>
        </form>

        {/* Help Section */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
            <Info className="w-4 h-4" />
            💡 Comment trouver l'IG et les glucides ?
          </h4>
          <div className="text-sm text-blue-700 space-y-2">
            <p><strong>Indice Glycémique (IG) :</strong></p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Consultez des tables d'IG officielles</li>
              <li>Utilisez des applications spécialisées</li>
              <li>Vérifiez sur l'emballage (rare mais possible)</li>
              <li>Recherchez dans la littérature scientifique</li>
            </ul>
            
            <p className="mt-3"><strong>Glucides :</strong></p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Lisez l'étiquette nutritionnelle</li>
              <li>Recherchez "Glucides" pour 100g</li>
              <li>Utilisez des bases de données nutritionnelles</li>
              <li>Applications comme MyFitnessPal ou Cronometer</li>
            </ul>
            
            <p className="mt-3 text-xs italic">
              💡 <strong>Astuce :</strong> L'IG peut varier selon la cuisson, la variété et la maturité de l'aliment.
            </p>
          </div>
        </div>

        {/* Educational Resources */}
        <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
          <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            📚 Ressources utiles
          </h4>
          <div className="text-sm text-green-700 space-y-1">
            <p>• <strong>Table internationale de l'IG</strong> (Université de Sydney)</p>
            <p>• <strong>Base CIQUAL</strong> (ANSES) pour les valeurs nutritionnelles</p>
            <p>• <strong>Diabète Québec</strong> - Guide des équivalences</p>
            <p>• <strong>Fédération Française des Diabétiques</strong> - Documentation</p>
          </div>
        </div>

        {/* Quick Examples */}
        <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <h4 className="font-medium text-yellow-800 mb-2">
            🔢 Exemples de valeurs courantes
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-yellow-700">
            <div>
              <p className="font-medium mb-1">IG bas (&lt; 55) :</p>
              <ul className="text-xs space-y-1">
                <li>• Légumes verts : IG 15-25</li>
                <li>• Légumineuses : IG 25-35</li>
                <li>• Pomme : IG 35</li>
                <li>• Avoine : IG 40</li>
              </ul>
            </div>
            <div>
              <p className="font-medium mb-1">IG élevé (&gt; 70) :</p>
              <ul className="text-xs space-y-1">
                <li>• Pain blanc : IG 75</li>
                <li>• Pomme de terre : IG 78</li>
                <li>• Riz blanc : IG 70</li>
                <li>• Glucose : IG 100</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFoodPage;